import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { 
  Upload, Search, Trash2, X, FileText, Download, 
  Grid, List, Filter, Tag, FolderOpen, Eye, Copy, Check,
  File, FileSpreadsheet, FileImage, Presentation,
  ShieldCheck, Archive
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentLibrary = ({ user }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerBlobUrl, setViewerBlobUrl] = useState(null);
  const [viewerFile, setViewerFile] = useState(null);
  const [viewerLoading, setViewerLoading] = useState(false);

  // Open a document inside an in-app viewer (no backend URL exposed)
  const openInViewer = async (file) => {
    setViewerFile(file);
    setViewerOpen(true);
    setViewerLoading(true);
    // Revoke any previous blob URL
    if (viewerBlobUrl) {
      try { window.URL.revokeObjectURL(viewerBlobUrl); } catch (_) {}
    }
    setViewerBlobUrl(null);
    try {
      const res = await axios.get(`${API}/documents/file/${file.filename}`, {
        headers: { 'Authorization': `Bearer ${user.token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: file.file_type || 'application/octet-stream' }));
      setViewerBlobUrl(url);
    } catch (e) {
      toast.error('Could not load document');
      setViewerOpen(false);
    } finally {
      setViewerLoading(false);
    }
  };

  const closeViewer = () => {
    if (viewerBlobUrl) {
      try { window.URL.revokeObjectURL(viewerBlobUrl); } catch (_) {}
    }
    setViewerBlobUrl(null);
    setViewerFile(null);
    setViewerOpen(false);
  };

  // In-app download — fetches blob, triggers <a download> programmatically (no URL bar exposure)
  const downloadInApp = async (file) => {
    try {
      const res = await axios.get(`${API}/documents/file/${file.filename}`, {
        headers: { 'Authorization': `Bearer ${user.token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: file.file_type || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_name || file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not download document');
    }
  };
  
  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('general');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadTags, setUploadTags] = useState('');
  const [uploadRelatedTo, setUploadRelatedTo] = useState('');
  const [uploadRelatedType, setUploadRelatedType] = useState('');
  const [recovering, setRecovering] = useState(false);
  const [backingUp, setBackingUp] = useState(false);

  const handleRecover = async () => {
    setRecovering(true);
    try {
      const res = await axios.post(`${API}/admin/migrate-files-to-gridfs`, {}, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      const { migrated_media, migrated_documents, skipped_already_in_gridfs } = res.data;
      if (migrated_media + migrated_documents > 0) {
        toast.success(`Recovered ${migrated_documents} documents (and ${migrated_media} images) to durable storage`);
      } else if (skipped_already_in_gridfs > 0) {
        toast.info(`All ${skipped_already_in_gridfs} files already in durable storage`);
      } else {
        toast.info('No files to recover');
      }
      fetchFiles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recovery failed');
    } finally {
      setRecovering(false);
    }
  };

  const handleBackup = async () => {
    setBackingUp(true);
    try {
      const res = await axios.get(`${API}/admin/backup/library?kind=documents`, {
        headers: { 'Authorization': `Bearer ${user.token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `documents_backup_${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Documents backup downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backup failed');
    } finally {
      setBackingUp(false);
    }
  };

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API}/documents/files?${params}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setFiles(response.data.files || []);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [user.token, selectedCategory, searchTerm]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/documents/categories`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setCategories(response.data.categories || []);
    } catch (error) {
      console.log('Could not fetch categories');
    }
  };

  useEffect(() => {
    fetchFiles();
    fetchCategories();
  }, [fetchFiles]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/plain',
        'text/csv'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        toast.error('Invalid file type. Please upload PDF, Word, Excel, PowerPoint, or Text files');
        return;
      }
      
      // Validate file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 50MB');
        return;
      }
      
      setUploadFile(file);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('category', uploadCategory);
      formData.append('description', uploadDescription);
      formData.append('tags', uploadTags);
      formData.append('related_to', uploadRelatedTo);
      formData.append('related_type', uploadRelatedType);

      await axios.post(`${API}/documents/upload`, formData, {
        headers: { 
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Document uploaded successfully');
      setShowUploadModal(false);
      resetUploadForm();
      fetchFiles();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadCategory('general');
    setUploadDescription('');
    setUploadTags('');
    setUploadRelatedTo('');
    setUploadRelatedType('');
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await axios.delete(`${API}/documents/files/${fileId}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Document deleted');
      fetchFiles();
      fetchCategories();
      setSelectedFile(null);
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const copyToClipboard = (url) => {
    const fullUrl = `${BACKEND_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedUrl(url);
    toast.success('URL copied to clipboard');
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return <FileText className="w-8 h-8 text-red-500" />;
    if (fileType?.includes('word') || fileType?.includes('document')) return <FileText className="w-8 h-8 text-blue-500" />;
    if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return <FileSpreadsheet className="w-8 h-8 text-green-500" />;
    if (fileType?.includes('powerpoint') || fileType?.includes('presentation')) return <Presentation className="w-8 h-8 text-orange-500" />;
    return <File className="w-8 h-8 text-slate-500" />;
  };

  const getFileExtension = (filename) => {
    return filename?.split('.').pop()?.toUpperCase() || 'FILE';
  };

  const categoryOptions = [
    { value: 'general', label: 'General' },
    { value: 'coa', label: 'COA (Certificate of Analysis)' },
    { value: 'invoice', label: 'Invoices' },
    { value: 'contract', label: 'Contracts' },
    { value: 'report', label: 'Reports' },
    { value: 'specification', label: 'Specifications' },
    { value: 'license', label: 'Licenses & Permits' },
    { value: 'marketing', label: 'Marketing Materials' }
  ];

  const relatedTypeOptions = [
    { value: '', label: 'None' },
    { value: 'order', label: 'Order' },
    { value: 'product', label: 'Product' },
    { value: 'supplier', label: 'Supplier' },
    { value: 'customer', label: 'Customer' }
  ];

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Document Library</h2>
          <p className="text-slate-500">Manage company documents, COAs, contracts and reports</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleRecover}
            disabled={recovering}
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="recover-docs-btn"
            title="Move legacy local files into durable MongoDB storage"
          >
            <ShieldCheck className="w-4 h-4 mr-2" />
            {recovering ? 'Recovering…' : 'Recover Files'}
          </Button>
          <Button
            onClick={handleBackup}
            disabled={backingUp}
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="backup-docs-btn"
            title="Download a ZIP of all documents"
          >
            <Archive className="w-4 h-4 mr-2" />
            {backingUp ? 'Preparing…' : 'Backup ZIP'}
          </Button>
          <Button
            onClick={() => setShowUploadModal(true)}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="upload-document-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Document
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white p-4 rounded-xl border border-slate-200">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
        >
          <option value="all">All Categories</option>
          {categoryOptions.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
      </div>

      {/* Category Pills */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {categories.map(cat => (
            <span 
              key={cat.name}
              className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors
                ${selectedCategory === cat.name 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              onClick={() => setSelectedCategory(selectedCategory === cat.name ? 'all' : cat.name)}
            >
              {cat.name} ({cat.count})
            </span>
          ))}
        </div>
      )}

      {/* Documents List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : files.length === 0 ? (
        <div className="text-center py-20 bg-slate-50 rounded-xl border border-dashed border-slate-300">
          <FileText className="w-12 h-12 mx-auto text-slate-400 mb-4" />
          <p className="text-slate-600 font-medium">No documents yet</p>
          <p className="text-slate-400 text-sm mt-1">Upload your first document to get started</p>
          <Button
            onClick={() => setShowUploadModal(true)}
            variant="outline"
            className="mt-4"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Document
          </Button>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Size</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Uploaded</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {files.map((file) => (
                <tr key={file.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-slate-100 rounded-lg">
                      {getFileIcon(file.file_type)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-700">{file.original_name}</p>
                    {file.description && (
                      <p className="text-xs text-slate-400 mt-0.5">{file.description}</p>
                    )}
                    {file.related_type && file.related_to && (
                      <p className="text-xs text-indigo-500 mt-0.5">
                        Linked to: {file.related_type} - {file.related_to}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded font-medium
                      ${file.category === 'coa' ? 'bg-green-100 text-green-700' : 
                        file.category === 'invoice' ? 'bg-blue-100 text-blue-700' :
                        file.category === 'contract' ? 'bg-purple-100 text-purple-700' :
                        'bg-slate-100 text-slate-600'}`}>
                      {file.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{formatFileSize(file.file_size)}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    <div>
                      {new Date(file.created_at).toLocaleDateString()}
                      <p className="text-xs text-slate-400">{file.uploaded_by}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openInViewer(file)}
                        className="p-1.5 hover:bg-slate-100 rounded"
                        title="View"
                        data-testid={`doc-view-${file.id}`}
                      >
                        <Eye className="w-4 h-4 text-slate-600" />
                      </button>
                      <button
                        onClick={() => downloadInApp(file)}
                        className="p-1.5 hover:bg-slate-100 rounded"
                        title="Download"
                        data-testid={`doc-download-${file.id}`}
                      >
                        <Download className="w-4 h-4 text-slate-600" />
                      </button>
                      <button
                        onClick={() => handleDelete(file.id)}
                        className="p-1.5 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {files.map((file) => (
              <div
                key={file.id}
                className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm"
                data-testid={`doc-card-${file.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-12 h-12 bg-slate-100 rounded-lg flex-shrink-0">
                    {getFileIcon(file.file_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-700 truncate">{file.original_name}</p>
                    {file.description && (
                      <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{file.description}</p>
                    )}
                    {file.related_type && file.related_to && (
                      <p className="text-xs text-indigo-500 mt-0.5 truncate">
                        Linked: {file.related_type} - {file.related_to}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className={`px-2 py-0.5 text-[10px] rounded font-medium
                        ${file.category === 'coa' ? 'bg-green-100 text-green-700' : 
                          file.category === 'invoice' ? 'bg-blue-100 text-blue-700' :
                          file.category === 'contract' ? 'bg-purple-100 text-purple-700' :
                          'bg-slate-100 text-slate-600'}`}>
                        {file.category}
                      </span>
                      <span className="text-[10px] text-slate-500">{formatFileSize(file.file_size)}</span>
                      <span className="text-[10px] text-slate-400">• {new Date(file.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                  <button
                    onClick={() => openInViewer(file)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-slate-50 hover:bg-slate-100 rounded-lg text-xs font-medium text-slate-700"
                    data-testid={`doc-view-mobile-${file.id}`}
                  >
                    <Eye className="w-3.5 h-3.5" /> View
                  </button>
                  <button
                    onClick={() => downloadInApp(file)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-indigo-50 hover:bg-indigo-100 rounded-lg text-xs font-medium text-indigo-700"
                    data-testid={`doc-download-mobile-${file.id}`}
                  >
                    <Download className="w-3.5 h-3.5" /> Download
                  </button>
                  <button
                    onClick={() => handleDelete(file.id)}
                    className="px-3 py-2 bg-red-50 hover:bg-red-100 rounded-lg text-red-600"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-slate-800">Upload Document</h3>
              <button onClick={() => { setShowUploadModal(false); resetUploadForm(); }}>
                <X className="w-5 h-5 text-slate-400 hover:text-slate-600" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* File dropzone */}
              <div 
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors
                  ${uploadFile ? 'border-indigo-300 bg-indigo-50' : 'border-slate-300 hover:border-indigo-400'}`}
                onClick={() => document.getElementById('doc-file-input').click()}
              >
                {uploadFile ? (
                  <div className="space-y-2">
                    <div className="mx-auto w-fit">
                      {getFileIcon(uploadFile.type)}
                    </div>
                    <p className="text-sm font-medium text-slate-700">{uploadFile.name}</p>
                    <p className="text-xs text-slate-400">{formatFileSize(uploadFile.size)}</p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                    <p className="text-slate-600">Click to select or drag & drop</p>
                    <p className="text-xs text-slate-400">PDF, Word, Excel, PowerPoint, Text (max 50MB)</p>
                  </>
                )}
                <input
                  id="doc-file-input"
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                <select
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                >
                  {categoryOptions.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description (optional)</label>
                <Input
                  type="text"
                  placeholder="Brief description of the document"
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tags (optional)</label>
                <Input
                  type="text"
                  placeholder="Comma-separated tags"
                  value={uploadTags}
                  onChange={(e) => setUploadTags(e.target.value)}
                />
              </div>

              {/* Link to Entity */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Link to</label>
                  <select
                    value={uploadRelatedType}
                    onChange={(e) => setUploadRelatedType(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    {relatedTypeOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                {uploadRelatedType && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Reference ID</label>
                    <Input
                      type="text"
                      placeholder="Order/Product ID"
                      value={uploadRelatedTo}
                      onChange={(e) => setUploadRelatedTo(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setShowUploadModal(false); resetUploadForm(); }}>
                Cancel
              </Button>
              <Button 
                onClick={handleUpload} 
                disabled={!uploadFile || uploading}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* In-app Document Viewer Modal — hides backend URL from address bar */}
      {viewerOpen && (
        <div className="fixed inset-0 bg-black/80 z-[70] flex flex-col" data-testid="doc-viewer-modal">
          {/* Header bar */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white">
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="w-5 h-5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{viewerFile?.original_name || 'Document'}</p>
                <p className="text-xs text-slate-300">{viewerFile?.category || ''}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => viewerFile && downloadInApp(viewerFile)}
                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 rounded text-xs font-medium flex items-center gap-1.5"
                data-testid="doc-viewer-download"
              >
                <Download className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Download</span>
              </button>
              <button
                onClick={closeViewer}
                className="p-1.5 hover:bg-slate-700 rounded"
                aria-label="Close"
                data-testid="doc-viewer-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content area */}
          <div className="flex-1 bg-slate-100 overflow-hidden">
            {viewerLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3"></div>
                  <p className="text-slate-600 text-sm">Loading document…</p>
                </div>
              </div>
            ) : viewerBlobUrl && viewerFile ? (
              (() => {
                const t = (viewerFile.file_type || '').toLowerCase();
                const isPdf = t.includes('pdf');
                const isImg = t.startsWith('image/');
                const isText = t.startsWith('text/') || t.includes('csv');
                if (isPdf) {
                  return (
                    <iframe
                      src={viewerBlobUrl}
                      className="w-full h-full border-0"
                      title="Document viewer"
                    />
                  );
                }
                if (isImg) {
                  return (
                    <div className="w-full h-full flex items-center justify-center p-4">
                      <img src={viewerBlobUrl} alt={viewerFile.original_name} className="max-w-full max-h-full object-contain" />
                    </div>
                  );
                }
                if (isText) {
                  return (
                    <iframe
                      src={viewerBlobUrl}
                      className="w-full h-full bg-white border-0"
                      title="Document viewer"
                    />
                  );
                }
                // For Word/Excel/PowerPoint etc — preview not supported by browsers
                return (
                  <div className="h-full flex items-center justify-center p-6">
                    <div className="text-center max-w-md">
                      <FileText className="w-16 h-16 mx-auto text-slate-400 mb-4" />
                      <p className="font-medium text-slate-700 mb-1">In-browser preview not available</p>
                      <p className="text-sm text-slate-500 mb-4">
                        {viewerFile.file_type || 'This file type'} can't be previewed inline. Download to view it.
                      </p>
                      <button
                        onClick={() => downloadInApp(viewerFile)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium"
                      >
                        <Download className="w-4 h-4" />
                        Download {viewerFile.original_name}
                      </button>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="flex items-center justify-center h-full text-slate-500">
                <p>Failed to load document</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentLibrary;
